import { useFieldApi } from '@data-driven-forms/react-form-renderer';
import { FormElementWidgetWithOptions } from 'lib-node-amb-renderer';
import { useEffect, useState } from 'react';

import FormControl from '~/common/componentMapper/FormControl';
import FormErrorMessage from '~/common/componentMapper/FormErrorMessage';
import FormLabel from '~/common/componentMapper/FormLabel';
import Box from '~/common/components/Box/index.js';
import Checkbox from '~/common/components/Checkboxes/Checkbox/Checkbox';
import CheckboxWrapper from '~/common/components/Checkboxes/CheckboxGroup';

export class CheckboxGroupClass extends FormElementWidgetWithOptions {
  hasNone;
  noneName;

  constructor(props) {
    super(props);
    this.hasNone = props?.hasNone;
    this.noneName = props?.noneName;
  }
}

type CheckboxGroupProps = {
  hasNone?: string;
  noneName?: string;
  name?: string;
  label?: string;
  noneLabel?: string;
  flStyle?: any;
  options?: {
    label?: string;
    value?: string | number;
    name?: string;
    sx?: any;
  }[];
  sx?: any;
  groupSx?: any;
};

const CheckboxGroup = ({
  name,
  label,
  hasNone,
  noneName = 'None',
  noneLabel = 'None',
  options = [],
  flStyle,
  sx = {},
  groupSx = {},
  ...props
}: Readonly<CheckboxGroupProps>) => {
  const { input, meta, isRequired } = useFieldApi({
    ...props,
    name,
    isRequired: true,
  });

  const [checkboxAnswers, setCheckboxAnswers] = useState(input.value);
  const [isDisabled, setIsDisabled] = useState(false);
  const [noneDisabled, setNoneDisabled] = useState(false);

  const disableOtherCheckboxOptions = (isTrue) => {
    setNoneDisabled(!isTrue);
    setIsDisabled(isTrue);
  };

  const disableCondition = () => {
    const userResponse = Object.values(checkboxAnswers).map(
      (value) => value !== noneName,
    );

    const noneResponse = Object.values(checkboxAnswers).map(
      (value) => value === noneName,
    );

    if (userResponse.includes(true)) {
      disableOtherCheckboxOptions(false);
    } else if (noneResponse[0] === true) {
      disableOtherCheckboxOptions(true);
    } else {
      setNoneDisabled(false);
      setIsDisabled(false);
    }
  };

  useEffect(() => {
    disableCondition();
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [checkboxAnswers]);

  const invalid = meta.touched && meta.invalid;
  const text = 'Required';

  return (
    <FormControl
      isRequired={isRequired}
      isInvalid={invalid}
      mb={'1rem'}
      sx={sx}
    >
      <FormLabel sx={{ ...flStyle }}>{label}</FormLabel>
      <CheckboxWrapper
        isInvalid={invalid}
        isRequired={isRequired}
        name={name}
        defaultValue={input.value}
      >
        <Box sx={groupSx} {...props}>
          {!!options.length &&
            options.map(({ label, value, name, sx }) => (
              <Checkbox
                isInvalid={invalid}
                key={label}
                value={value}
                name={name}
                sx={sx}
                isChecked={true}
                isDisabled={isDisabled}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  let userSelectionArray;
                  if (isChecked) {
                    userSelectionArray = [...checkboxAnswers, value];
                  } else {
                    userSelectionArray = checkboxAnswers.filter(
                      (item) => item !== value,
                    );
                  }
                  setCheckboxAnswers(userSelectionArray);
                  input.onChange(userSelectionArray);
                }}
              >
                {label}
              </Checkbox>
            ))}

          {hasNone && (
            <Checkbox
              isDisabled={noneDisabled}
              isInvalid={invalid}
              name={noneName}
              value={noneName}
              onChange={(e) => {
                const isChecked = e.target.checked;
                let noneSelection;
                if (isChecked) {
                  noneSelection = [...checkboxAnswers, noneName];
                } else {
                  noneSelection = [];
                }
                setCheckboxAnswers(noneSelection);
                input.onChange(noneSelection);
              }}
            >
              {noneLabel}
            </Checkbox>
          )}
        </Box>
      </CheckboxWrapper>
      {meta.invalid && <FormErrorMessage>{text}</FormErrorMessage>}
    </FormControl>
  );
};

export default CheckboxGroup;
